import { useCallback, useEffect, useState } from "react";

/**
 * useScrollEdges — "there is more content this way", for a horizontal scroll
 * container.
 *
 * It lives here for the reason `useColumnResize` does: measuring a scroll
 * position is a self-contained problem, and `AppDataTable` is already the file
 * people open to learn how a column behaves. The barrel exports it for a rail
 * that needs arrows (`Dashboard/StudentHome/cards/RecommendedRail.jsx`).
 *
 * It measures on scroll AND under a ResizeObserver, because the answer changes
 * when the container resizes and nobody scrolled.
 *
 * ⚠️ It observes the CONTENT as well as the container. A `resizable` table
 * swaps `width: 100%` for `width: <total>px` the moment `useColumnResize` seeds
 * its widths, and that changes `scrollWidth` without touching the container at
 * all — so a container-only observer would report "nothing to scroll" on
 * exactly the tables that have most to scroll.
 *
 * ⚠️ State is written only when the answer CHANGES. A scroll handler that calls
 * setState every tick re-renders the whole table for the length of a drag;
 * `StudentCourseDetail` guards the same way, for the same reason.
 *
 * ⚠️ LTR only. `scrollLeft` is negative under `direction: rtl` in some engines
 * and both edges would read backwards. The app has no RTL surface today.
 *
 * It also reports `viewport` — the scroll container's own visible width. That
 * is not a scroll edge, but it is measured from the same element by the same
 * observer, and it is the number a full-width row inside the table needs: a
 * `colSpan` cell on a table wider than the screen is centred on the TABLE, so
 * its content can sit entirely off-screen. `AppDataTable`'s empty row pins
 * itself to `left: 0` at this width instead. A second ResizeObserver on the
 * same element, in the same component, would be the alternative.
 *
 * @param {React.RefObject<HTMLElement>} ref the scroll container
 * @returns {{ left: boolean, right: boolean, viewport: number, onScroll: () => void }}
 */

/** Sub-pixel slack. At 150 % zoom — the documented admin viewport —
 *  `scrollWidth` and `clientWidth` differ by a fraction of a pixel on a table
 *  that fits, which without this reads as "there is more to the right". */
const SLACK = 2;

export default function useScrollEdges(ref) {
  const [edges, setEdges] = useState({ left: false, right: false, viewport: 0 });

  const measure = useCallback(() => {
    const el = ref?.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = {
      left: el.scrollLeft > SLACK,
      right: max > SLACK && el.scrollLeft < max - SLACK,
      // Rounded, so a fractional client width at 150 % zoom does not write
      // state on every scroll tick — see the note above about re-renders.
      viewport: Math.round(el.clientWidth),
    };
    setEdges((prev) =>
      prev.left === next.left && prev.right === next.right && prev.viewport === next.viewport
        ? prev
        : next
    );
  }, [ref]);

  useEffect(() => {
    const el = ref?.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The table itself — see the width-swap warning above.
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [ref, measure]);

  return { ...edges, onScroll: measure };
}
